import {
  faAngleDown,
  faAngleRight,
  faAngleUp,
  faArrowLeft,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import MuiDrawer from '@mui/material/Drawer';
import React, { useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DaxkoLogo from 'src/assets/img/daxko-logo.svg';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { List, ListItem, ListItemProps } from 'src/components/ListItem';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Theme } from 'src/components/muiTheme/types';
import {
  ClickAwayListener,
  SxProps,
  useMediaQuery,
} from 'src/components/muiTheme/utils';
import { Collapse } from 'src/components/transitions/Collapse';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { isTrainer } from 'src/models/user/utils';
import { useIsAdminPortal } from 'src/shared/dreamcoat';

import { ActionsMenuDreamcoat } from './ActionsMenuDreamcoat';
import { MessagesIndicator } from './MessagesIndicator';
import { TaskCountIndicator } from './TaskCountIndicator';
import {
  DecoratedNavItem,
  getNavItemChildren,
  hasNavItemChildren,
  isSectionedNavChildren,
} from './navUtils';
import { getSolidIcon } from './routeConfig';
import { useAdminNav } from './useAdminNav';
import { useMainNav } from './useMainNav';

export const TrainerNavColors = {
  applicationMenuBg: '#014271',
  applicationMenuItemHighlight: '#025B9C',
  applicationMenuItemHoverBg: '#025B9C80',
  applicationMenuBorder: 'transparent',
  applicationMenuSelectedBorder: '#038BED',
  primaryHighlight: '#038BED',
  submenuBg: '#232236',
  submenuItemTitle: '#C0C0C6',
  submenuItemBottomBorder: 'rgba(255, 255, 255, 0.13)',
  submenuItemHoverBg: '#025B9C',
  submenuItemActiveBorder: '#038BED',
  text: 'white',
};

// Using our palettes (more exact)
export const ClientNavColors = {
  applicationMenuBg: 'neutral.1',
  applicationMenuItemHighlight: 'neutral.4',
  applicationMenuItemHoverBg: 'neutral.4',
  applicationMenuBorder: 'neutral.5',
  applicationMenuSelectedBorder: 'neutral.10',
  primaryHighlight: 'neutral.10',
  submenuBg: 'neutral.1',
  submenuItemTitle: 'text.primary',
  submenuItemBottomBorder: 'neutral.4',
  submenuItemHoverBg: 'neutral.4',
  submenuItemActiveBorder: 'neutral.10',
  text: 'text.primary',
};

// Staff should always see the Daxko logo; only clients see the customer icon
export const getNavLogo = (
  isClientNav: boolean,
  session?: { iconUrl?: string; brand?: string }
) => {
  if (isClientNav && session?.iconUrl) {
    return { src: session.iconUrl, alt: session.brand || 'Logo' };
  }
  return { src: DaxkoLogo, alt: 'Daxko Logo' };
};

export interface MainNavProps {
  open: boolean;
  toggleOpen: () => void;
  customNavItems?: DecoratedNavItem[];
  navWidth?: string;
}

export const MainNavDreamcoat: React.FC<MainNavProps> = ({
  customNavItems,
  ...props
}) => {
  const isAdminPortal = useIsAdminPortal();

  if (isAdminPortal) {
    return (
      <MainNavDreamcoatContent
        {...props}
        isAdmin
        navItemsToRender={[]}
      />
    );
  }

  return <TenantMainNavDreamcoat {...props} customNavItems={customNavItems} />;
};

const TenantMainNavDreamcoat: React.FC<MainNavProps> = ({
  customNavItems,
  ...props
}) => {
  const mainNavItems = useMainNav();
  const navItemsToRender = customNavItems || mainNavItems;

  return (
    <MainNavDreamcoatContent
      {...props}
      isAdmin={false}
      navItemsToRender={navItemsToRender}
    />
  );
};

interface MainNavDreamcoatContentProps
  extends Omit<MainNavProps, 'customNavItems'> {
  isAdmin: boolean;
  navItemsToRender: DecoratedNavItem[];
}

const MainNavDreamcoatContent: React.FC<MainNavDreamcoatContentProps> = ({
  open,
  toggleOpen,
  navWidth = '84px',
  isAdmin,
  navItemsToRender,
}) => {
  const [activeSubmenu, setActiveSubmenu] = useState<DecoratedNavItem | null>(
    null
  );
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<
    Record<string, boolean>
  >({});
  const { session, user } = useOptionalSession();

  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const toggleSection = (sectionId: string) => {
    setCollapsedSectionIds((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  const isTrainerNav = isAdmin || isTrainer(user || undefined);

  // For the trainer nav which doesnt switch for dark/light mode, we need to force the dark mode class on the nav so that the icons are white.
  const forceDarkmodeClassForTrainer = isTrainerNav ? 'dark' : '';
  const DreamcoatNavColors = isTrainerNav ? TrainerNavColors : ClientNavColors;

  const { t } = useTranslation('translation');
  const isClientNav = !!user && !isTrainer(user) && !isAdmin;
  const navBrandTitle = isClientNav ? session?.brand : t('Exercise');
  const navLogo = getNavLogo(isClientNav, session);

  return (
    <ClickAwayListener
      onClickAway={() => activeSubmenu && setActiveSubmenu(null)}
    >
      <MuiDrawer
        open={open}
        anchor="left"
        disableEnforceFocus
        PaperProps={{
          elevation: 0,
          sx: { bgcolor: 'transparent', p: 0 },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: smDown ? '100%' : navWidth,
            height: '100%',
            p: 0,
            overflow: 'visible',
          },
          '&& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, .15)',
          },
        }}
        onClose={toggleOpen}
        variant={smDown ? 'temporary' : 'permanent'}
      >
        <Stack
          direction="column"
          sx={{
            width: smDown ? '100%' : navWidth,
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            zIndex: 2,
            position: 'relative',
            bgcolor: DreamcoatNavColors.applicationMenuBg,
            borderRight: `1px solid`,
            borderRightColor: DreamcoatNavColors.applicationMenuBorder,
            //overflowY: 'auto',
          }}
        >
          {smDown ? (
            <Stack
              justifyContent={'space-between'}
              sx={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2, py: 1.5 }}
                >
                  <Stack direction="row" alignItems="center">
                    <img
                      src={navLogo.src}
                      alt={navLogo.alt}
                      width="24px"
                      height="24px"
                    />
                    <Typography
                      color={DreamcoatNavColors.text}
                      variant="h5"
                      fontWeight={400}
                      sx={{ ml: 1 }}
                    >
                      {navBrandTitle}
                    </Typography>
                  </Stack>

                  <IconButton
                    className={forceDarkmodeClassForTrainer}
                    onClick={toggleOpen}
                  >
                    <Icon size="small" icon={faTimes} />
                  </IconButton>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2.5, pb: 2 }}
                >
                  <Typography color={DreamcoatNavColors.text} variant="body1">
                    {isAdmin ? 'Exercise.com Admin Portal' : session?.brand}
                  </Typography>

                  {!isAdmin && (
                    <div className={forceDarkmodeClassForTrainer}>
                      <ActionsMenuDreamcoat size="small" hideUserMenu />
                    </div>
                  )}
                </Stack>

                <Scroller
                  direction="column"
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: 'auto',
                    maxHeight: 'none',
                    width: '100%',
                  }}
                >
                  {isAdmin ? (
                    <AdminNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                      toggleOpen={toggleOpen}
                    />
                  ) : (
                    <MainNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                      toggleOpen={toggleOpen}
                      navItems={navItemsToRender}
                      placement="top"
                    />
                  )}
                </Scroller>
              </Box>

              {!isAdmin && (
                <BottomNavItems
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  navItems={navItemsToRender}
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Stack>
          ) : (
            <>
              <Stack
                direction="column"
                alignItems="center"
                sx={{
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box sx={{ mt: 2, mb: 2.25 }}>
                  <img
                    src={navLogo.src}
                    alt={navLogo.alt}
                    width="40px"
                    height="40px"
                  />
                </Box>

                <Scroller
                  direction="column"
                  hideScrollbar
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: 'auto',
                    maxHeight: 'none',
                    overflowY: 'auto',
                    overflowX: 'clip',
                    width: '100%',
                  }}
                >
                  {isAdmin ? (
                    <AdminNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                      toggleOpen={toggleOpen}
                    />
                  ) : (
                    <MainNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                      toggleOpen={toggleOpen}
                      navItems={navItemsToRender}
                      placement="top"
                    />
                  )}
                </Scroller>
              </Stack>

              {!isAdmin && (
                <BottomNavItems
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  navItems={navItemsToRender}
                />
              )}
            </>
          )}
        </Stack>

        <Stack
          sx={{
            bgcolor: DreamcoatNavColors.submenuBg,
            borderRight: `1px solid`,
            borderRightColor: DreamcoatNavColors.applicationMenuBorder,
            p: 0,
            transform: activeSubmenu ? 'translateX(0)' : 'translateX(-100%)',
            opacity: activeSubmenu ? 1 : 0,
            pointerEvents: activeSubmenu ? 'auto' : 'none',
            transition: 'all 0.2s ease-in-out',
            zIndex: smDown ? 4 : 1,
            position: 'absolute',
            top: '0px',
            left: smDown ? '0px' : navWidth,
            height: '100%',
            width: smDown ? '100%' : '280px',
          }}
        >
          {smDown ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1 }}
            >
              <Stack direction="row" alignItems="center" sx={{ ml: -1 }}>
                <IconButton
                  className={forceDarkmodeClassForTrainer}
                  onClick={() => setActiveSubmenu(null)}
                >
                  <Icon size="small" icon={faArrowLeft} />
                </IconButton>
                <Typography color={DreamcoatNavColors.text} variant="h5">
                  {activeSubmenu?.name}
                </Typography>
              </Stack>
              <IconButton
                className={forceDarkmodeClassForTrainer}
                onClick={() => {
                  toggleOpen();
                  setActiveSubmenu(null);
                }}
              >
                <Icon size="small" icon={faTimes} />
              </IconButton>
            </Stack>
          ) : (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography
                color={DreamcoatNavColors.text}
                fontWeight={600}
                sx={{ fontSize: '24px', lineHeight: '32px' }}
              >
                {navBrandTitle}
              </Typography>
              <Typography
                color={DreamcoatNavColors.text}
                variant="h6"
                fontWeight={400}
                sx={{ fontSize: '16px', lineHeight: '24px' }}
              >
                {activeSubmenu?.name}
              </Typography>
            </Box>
          )}
          <Scroller
            direction="column"
            sx={{ px: 1, pb: 2, pt: 1.5, maxHeight: '100%' }}
          >
            <SubmenuItems
              activeSubmenu={activeSubmenu}
              collapsedSectionIds={collapsedSectionIds}
              setActiveSubmenu={setActiveSubmenu}
              toggleSection={toggleSection}
              toggleOpen={toggleOpen}
            />
          </Scroller>
        </Stack>
      </MuiDrawer>
    </ClickAwayListener>
  );
};

export interface TopLevelNavItemProps {
  title: string;
  route?: string;
  reactRoute?: string;
  icon?: ListItemProps['icon'];
  childrenItems?: DecoratedNavItem[];
  onClick?: () => void;
  subnavOpen?: boolean;
  target?: string;
  rel?: string;
  level?: number;
  subItem?: boolean;
}

const normalizePath = (path?: string) => path?.replace(/\/$/, '');

const isExternalNavItem = ({
  reactRoute,
  route,
  target,
}: Pick<TopLevelNavItemProps, 'reactRoute' | 'route' | 'target'>) => {
  return target === '_blank' && !!(reactRoute || route);
};

const getNavItemPath = ({
  reactRoute,
  route,
  target,
}: Pick<TopLevelNavItemProps, 'reactRoute' | 'route' | 'target'>) => {
  const isOutsideLink = isExternalNavItem({ reactRoute, route, target });
  let path =
    reactRoute ||
    (route ? `${isOutsideLink ? '' : '/embed'}${route}` : undefined);

  // hack because of bad route data
  if (path?.startsWith('/embed/dashboard/fbm/schedule')) {
    path = '/embed/fbm/schedule/';
  }

  return path;
};

// Routes whose nav item should also highlight on sub-paths (e.g. /me/journal
// for the React journal day/month views, and /embed/dashboard/journal for the
// v2 embed when it surfaces sub-paths). Kept as an explicit allow-list so we
// don't accidentally double-highlight sibling tabs like /products vs
// /products/inventory.
const PREFIX_MATCH_PATHS = ['/me/journal', '/embed/dashboard/journal'];

const isNavItemSelected = (
  pathname: string,
  item: Pick<TopLevelNavItemProps, 'reactRoute' | 'route' | 'target'>
) => {
  const path = getNavItemPath(item);
  if (!path) return false;

  const normalizedPath = normalizePath(path);
  const normalizedPathname = normalizePath(pathname);
  if (!normalizedPath || !normalizedPathname) return false;

  if (normalizedPathname === normalizedPath) return true;

  return (
    PREFIX_MATCH_PATHS.includes(normalizedPath) &&
    normalizedPathname.startsWith(`${normalizedPath}/`)
  );
};

const hasSelectedChild = (
  pathname: string,
  childrenItems?: DecoratedNavItem[]
): boolean => {
  return !!childrenItems?.some((child) => {
    return (
      isNavItemSelected(pathname, child) ||
      hasSelectedChild(pathname, getNavItemChildren(child))
    );
  });
};

export const TopLevelNavItem: React.FC<TopLevelNavItemProps> = ({
  title,
  icon,
  route,
  reactRoute,
  childrenItems,
  onClick,
  subnavOpen,
  target,
  rel,
  subItem,
}) => {
  const opensSubmenu = !route && !reactRoute;
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useOptionalSession();
  const isAdminPortal = useIsAdminPortal();
  // Only used when NavItem has children
  const [, startTransition] = useTransition();

  const { pathname } = location;
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const isTrainerNav = isAdminPortal || isTrainer(user || undefined);
  const DreamcoatNavColors = isTrainerNav ? TrainerNavColors : ClientNavColors;
  const navTextColor = isTrainerNav
    ? 'primary.contrastText'
    : DreamcoatNavColors.text;

  const navItemStyles = () => {
    return {
      p: 0,
      py: 1.1,
      m: 0,
      display: 'flex',
      gap: 0.5,
      px: { xs: 2, sm: 0 },
      flexDirection: { xs: 'row', sm: 'column' },
      color: navTextColor,
      position: 'relative',
      width: '100%',
      '&&&& .MuiListItemIcon-root svg': {
        color: navTextColor,
        fontSize: '16px',
        mb: { xs: 0, sm: 0.5 },
      },
      '&&&& .MuiListItemText-root .MuiTypography-root': {
        color: navTextColor,
        fontSize: { xs: '16px', sm: '10px' },
        lineHeight: { xs: '1.2em', sm: '12px' },
        fontWeight: { xs: 400, sm: 600 },
        textAlign: { xs: 'left', sm: 'center' },
        px: 1,
      },
      ...(subnavOpen && {
        bgcolor: DreamcoatNavColors.applicationMenuItemHoverBg,
      }),
      '&:hover': {
        bgcolor: DreamcoatNavColors.applicationMenuItemHoverBg,
      },
      '&&.Mui-selected': {
        bgcolor: DreamcoatNavColors.applicationMenuItemHighlight,
        ...(isTrainerNav
          ? {
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: DreamcoatNavColors.primaryHighlight,
            }
          : {
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: DreamcoatNavColors.applicationMenuSelectedBorder,
            }),
      },
      ...(opensSubmenu && subnavOpen
        ? {
            '*:after': {
              content: '""',
              position: 'absolute',
              top: 'calc(50% - 15px)',
              right: '-2px',
              width: '0px',
              height: '0px',
              borderTop: '15px solid transparent',
              borderBottom: '15px solid transparent',
              borderRight: `10px solid`,
              borderRightColor: DreamcoatNavColors.submenuBg,
            },
          }
        : {}),
    };
  };

  const subItemStyles = () => {
    const subItemTextColor = isTrainerNav
      ? 'primary.contrastText'
      : DreamcoatNavColors.submenuItemTitle;

    return {
      p: 0,
      flexGrow: 0,
      minHeight: '36px',
      borderBottom: '1px solid',
      borderColor: DreamcoatNavColors.submenuItemBottomBorder,
      '& .MuiListItemText-root .MuiTypography-root': {
        color: subItemTextColor,
        fontSize: '14px',
        lineHeight: '16px',
        fontWeight: 300,
        p: '10px',
        display: 'flex',
        alignItems: 'center',
      },
      '&.Mui-selected': {
        bgcolor: DreamcoatNavColors.submenuItemHoverBg,
        borderLeft: '3px solid',
        borderLeftColor: DreamcoatNavColors.submenuItemActiveBorder,
      },
      '&:hover': {
        bgcolor: DreamcoatNavColors.submenuItemHoverBg,
      },
      '&.Mui-selected:hover': {
        bgcolor: DreamcoatNavColors.submenuItemHoverBg,
      },
    };
  };

  const isMessages = title === 'Messages';
  const isTaskManagement = reactRoute === '/people/tasks';

  const isOutsideLink = isExternalNavItem({ reactRoute, route, target });
  const path = getNavItemPath({ reactRoute, route, target });
  const isRootPath =
    pathname == '/embed/dashboard' &&
    path?.startsWith('/embed/dashboard/trainer-dashboard');

  const selected =
    isNavItemSelected(pathname, { reactRoute, route, target }) ||
    hasSelectedChild(pathname, childrenItems) ||
    isRootPath;
  const displayIcon = selected ? getSolidIcon(icon) : icon;

  const handleClick = (event?: React.MouseEvent) => {
    if (onClick) {
      onClick();
    }
    if (isOutsideLink) {
      return;
    }

    if (path) {
      event?.preventDefault();
      startTransition(() => {
        navigate(path);
      });
    }
  };

  return (
    <ListItem
      component={isOutsideLink ? 'a' : path ? Link : 'button'}
      {...(isOutsideLink ? { href: path } : path ? { to: path } : {})}
      onClick={handleClick}
      target={target}
      sx={subItem ? subItemStyles() : navItemStyles()}
      primary={title}
      icon={displayIcon}
      selected={selected}
      rightAdornment={
        opensSubmenu || isMessages || isTaskManagement ? (
          <>
            {isMessages && <MessagesIndicator />}
            {isTaskManagement && <TaskCountIndicator />}
            {opensSubmenu && smDown && (
              <Icon color="inherit" size="xsmall" icon={faAngleRight} />
            )}
          </>
        ) : undefined
      }
      rel={rel}
      clickable
      // To force embed page to update when navigating
      // between pages that are both on the embed route
      state={
        target === '_blank'
          ? undefined
          : {
              renderTime: Date.now(),
            }
      }
    />
  );
};

const SubmenuItems = ({
  activeSubmenu,
  collapsedSectionIds,
  setActiveSubmenu,
  toggleSection,
  toggleOpen,
}: {
  activeSubmenu: DecoratedNavItem | null;
  collapsedSectionIds: Record<string, boolean>;
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  toggleSection: (sectionId: string) => void;
  toggleOpen: () => void;
}) => {
  const { user } = useOptionalSession();
  const isAdminPortal = useIsAdminPortal();
  const isTrainerNav = isAdminPortal || isTrainer(user || undefined);
  const DreamcoatNavColors = isTrainerNav ? TrainerNavColors : ClientNavColors;

  const renderSubItem = (subItem: DecoratedNavItem, index: number) => {
    if (!subItem.enabled) {
      return null;
    }

    return (
      <TopLevelNavItem
        key={subItem.id + index}
        title={subItem.name}
        route={subItem.route}
        reactRoute={subItem.reactRoute}
        onClick={() => {
          setActiveSubmenu(null);
          toggleOpen();
        }}
        target={subItem.target}
        rel={subItem.rel}
        subItem
      />
    );
  };

  if (!activeSubmenu?.children) {
    return null;
  }

  if (!isSectionedNavChildren(activeSubmenu.children)) {
    return <>{getNavItemChildren(activeSubmenu).map(renderSubItem)}</>;
  }

  return (
    <>
      {(activeSubmenu.children.items || []).map(renderSubItem)}
      {(activeSubmenu.children.sections || []).map((section) => {
        const sectionKey = `${activeSubmenu.id}-${section.id}`;
        const collapsed =
          section.collapsible && collapsedSectionIds[sectionKey];

        return (
          <Box key={section.id} sx={{ '& + &': { mt: 1.5 } }}>
            <ListItem
              primary={section.label || section.name || section.id}
              onClick={() => section.collapsible && toggleSection(sectionKey)}
              rightAdornment={
                section.collapsible ? (
                  <Icon
                    color="inherit"
                    size="xsmall"
                    icon={collapsed ? faAngleDown : faAngleUp}
                    sx={{ color: DreamcoatNavColors.submenuItemTitle }}
                  />
                ) : undefined
              }
              sx={{
                px: 1,
                py: 1,
                color: 'primary.contrastText',
                '& .MuiListItemText-root .MuiTypography-root': {
                  color: DreamcoatNavColors.submenuItemTitle,
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '20px',
                },
              }}
              clickable={!!section.collapsible}
            />
            <Collapse in={!collapsed}>
              {section.children.map(renderSubItem)}
            </Collapse>
          </Box>
        );
      })}
    </>
  );
};

const AdminNavItems = ({
  setActiveSubmenu,
  activeSubmenu,
  toggleOpen,
}: {
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  activeSubmenu: DecoratedNavItem | null;
  toggleOpen?: () => void;
}) => {
  const adminNavItems = useAdminNav();

  const renderNavItems = (items: DecoratedNavItem[], level = 0) => {
    return items.map((navItem, i) => {
      if (!navItem.enabled) return null;
      const opensSubmenu = hasNavItemChildren(navItem);
      return (
        <TopLevelNavItem
          key={navItem.id + i}
          title={navItem.name}
          icon={level === 0 ? navItem.icon : undefined}
          childrenItems={getNavItemChildren(navItem)}
          subnavOpen={activeSubmenu?.id === navItem.id}
          route={opensSubmenu ? undefined : navItem.route}
          reactRoute={opensSubmenu ? undefined : navItem.reactRoute}
          onClick={() => {
            if (opensSubmenu) {
              setActiveSubmenu(navItem);
              return;
            }
            setActiveSubmenu(null);
            toggleOpen && toggleOpen();
          }}
          target={navItem.target}
          rel={navItem.rel}
          level={level}
        ></TopLevelNavItem>
      );
    });
  };

  return (
    <List sx={{ m: 0, width: '100%', py: 0 }}>
      {renderNavItems(adminNavItems)}
    </List>
  );
};

const BottomNavItems = ({
  setActiveSubmenu,
  activeSubmenu,
  navItems,
  sx,
}: {
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  activeSubmenu: DecoratedNavItem | null;
  navItems: DecoratedNavItem[];
  sx?: SxProps<Theme>;
}) => {
  const hasBottomItems = navItems.some(
    (navItem) => navItem.enabled && navItem.placement === 'bottom'
  );

  if (!hasBottomItems) {
    return null;
  }

  return (
    <Stack
      direction="column"
      alignItems="stretch"
      sx={[
        { boxShadow: '0px 0px 20px 0px #00000080', py: 1 },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <MainNavItems
        activeSubmenu={activeSubmenu}
        setActiveSubmenu={setActiveSubmenu}
        navItems={navItems}
        placement="bottom"
      />
    </Stack>
  );
};

const MainNavItems = ({
  setActiveSubmenu,
  activeSubmenu,
  navItems,
  placement,
  toggleOpen,
}: {
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  activeSubmenu: DecoratedNavItem | null;
  navItems: DecoratedNavItem[];
  toggleOpen?: () => void;
  placement: 'top' | 'bottom';
}) => {
  const renderNavItems = (items: DecoratedNavItem[], level = 0) => {
    return items
      .filter(
        (navItem) =>
          navItem.enabled &&
          (navItem.placement === 'bottom') === (placement === 'bottom')
      )
      .map((navItem, i) => {
        const opensSubmenu = hasNavItemChildren(navItem);
        return (
          <TopLevelNavItem
            key={navItem.id + i}
            title={navItem.name}
            icon={level === 0 ? navItem.icon : undefined}
            childrenItems={getNavItemChildren(navItem)}
            subnavOpen={activeSubmenu?.id === navItem.id}
            route={opensSubmenu ? undefined : navItem.route}
            reactRoute={opensSubmenu ? undefined : navItem.reactRoute}
            onClick={() => {
              if (opensSubmenu) {
                setActiveSubmenu(navItem);
                return;
              }
              toggleOpen && toggleOpen();
              setActiveSubmenu(null);
            }}
            target={navItem.target}
            rel={navItem.rel}
            level={level}
          ></TopLevelNavItem>
        );
      });
  };

  return (
    <List sx={{ m: 0, width: '100%', py: 0 }}>{renderNavItems(navItems)}</List>
  );
};
